import { useEffect } from 'react';
import { LogOut, Moon, PawPrint, Sun } from 'lucide-react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { StepRail, type Step, type StepStatus } from '@/components/step-rail';
import { use_session_store } from '@/lib/session-store';
import { use_theme_store } from '@/lib/theme-store';
import { use_draft } from '@/features/onboarding/hooks';
import type { DraftSummary } from '@/features/onboarding/types';
import {
  module_path,
  module_slug,
  module_steps_for_draft,
} from '@/features/onboarding/module-step-router';

type StepDef = { key: string; label: string; to: string; done_when: (d: DraftSummary | null | undefined) => boolean };

const STATIC_BEFORE_MODULES: StepDef[] = [
  {
    key: 'start',
    label: 'Select modules',
    to: '/onboarding/start',
    done_when: (d) => !!d,
  },
  {
    key: 'basic-info',
    label: 'Business details',
    to: '/onboarding/basic-info',
    done_when: (d) => !!d?.basic_info_completed,
  },
  {
    key: 'address',
    label: 'Address & location',
    to: '/onboarding/address',
    done_when: (d) => !!d?.address_completed,
  },
  {
    key: 'uploads',
    label: 'Documents & photos',
    to: '/onboarding/uploads',
    // Done once at least one KYC doc OR photo has landed (in-flight count
    // pre-submit, attached count post-submit).
    done_when: (d) =>
      ((d?.kyc_doc_count ?? 0) + (d?.photo_count ?? 0)) > 0,
  },
];

const STATIC_AFTER_MODULES: StepDef[] = [
  {
    key: 'payout',
    label: 'Payout details',
    to: '/onboarding/payout',
    // `payout_skipped` is deliberately not treated as done. It is a legacy column from when the
    // step was optional; a skipped payout is exactly the state that used to let a vendor be
    // approved with nowhere to be paid.
    done_when: (d) => !!d?.payout_completed,
  },
  {
    key: 'terms',
    label: 'Accept terms',
    to: '/onboarding/terms',
    done_when: (d) => !!d?.terms_completed,
  },
  {
    key: 'submit',
    label: 'Review & submit',
    to: '/onboarding/submit',
    done_when: (d) => !!d?.submitted_at,
  },
];

export function OnboardingShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const phone = use_session_store((s) => s.phone);
  const clear = use_session_store((s) => s.clear);
  const dark = use_theme_store((s) => s.dark);
  const toggle_theme = use_theme_store((s) => s.toggle);
  const draft_q = use_draft();

  function handle_sign_out() {
    clear();
    navigate('/login', { replace: true });
  }

  // A submitted application is read-only — funnel every step to the
  // submitted-status view so the vendor can't edit (or re-submit) it.
  const is_submitted = !!draft_q.data?.submitted_at;
  useEffect(() => {
    if (is_submitted && !location.pathname.startsWith('/onboarding/submit')) {
      navigate('/onboarding/submit', { replace: true });
    }
  }, [is_submitted, location.pathname, navigate]);

  const module_steps = module_steps_for_draft(draft_q.data);
  const dynamic_steps: StepDef[] = module_steps.map((m) => ({
    key: `module-${module_slug(m)}`,
    label: `Details · ${m}`,
    to: module_path(m),
    // Backend exposes per-module completion in DraftSummary.
    done_when: (d) => d?.module_details_completed?.[m] === true,
  }));
  const all_steps: StepDef[] = [
    ...STATIC_BEFORE_MODULES,
    ...dynamic_steps,
    ...STATIC_AFTER_MODULES,
  ];

  const steps: Step[] = all_steps.map((s): Step => {
    const is_active = location.pathname.startsWith(s.to);
    let status: StepStatus = 'todo';
    if (s.done_when(draft_q.data)) status = 'done';
    if (is_active) status = 'active';
    return { key: s.key, label: s.label, to: s.to, status };
  });

  return (
    <div className="page-gradient flex min-h-svh flex-col">
      <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-border bg-card/80 px-5 backdrop-blur-md">
        <div className="flex h-9 w-9 items-center justify-center rounded-button accent-gradient text-white">
          <PawPrint className="h-5 w-5" />
        </div>
        <div className="flex flex-col leading-tight">
          <span className="font-heading text-base font-bold">Vendor Console</span>
          <span className="text-xs text-muted-foreground" data-testid="session-phone">
            Signed in as {phone ?? 'unknown'}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto"
          onClick={toggle_theme}
          aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
          data-testid="theme-toggle"
        >
          {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handle_sign_out}
          data-testid="sign-out"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </Button>
      </header>

      <div className="mx-auto grid w-full max-w-6xl flex-1 gap-6 p-5 md:grid-cols-[260px_1fr]">
        <aside className="md:sticky md:top-[88px] md:h-fit">
          <StepRail steps={steps} />
        </aside>
        <main data-testid="onboarding-main" className="min-w-0">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
